import Alert from '@mui/material/Alert';
import { Theme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { updateEmploymentInfo } from '@/api/user';

import { Icon } from '@/components/Icon/Icon';
import { DrawerProps } from '@/components/Layout/Drawer';
import { FullPageLoaderOverlay } from '@/components/Layout/FullPageLoader';

import { LISTED_CATEGORIES_QUESTION } from '@/constants/document';

import { useAppSelector } from '@/hooks/redux';
import { useMutationWithUserData } from '@/hooks/useMutationWithUserData';
import { useTranslate } from '@/hooks/useTranslate';

import { selectLoggedInUser } from '@/redux/auth/selectors';
import { selectConfigsState } from '@/redux/configs';

import { Option } from '@/types/components/common';
import { EmploymentStatus, Profession } from '@/types/user';

import { getAPIErrorMessage } from '@/utils/api';
import { isNil } from '@/utils/common';
import {
  getEmploymentStatusOptions,
  getProfessionQuestionForSelfEmployed,
  getUnemployedProfessions,
  ProfessionQuestionConfig,
} from '@/utils/employment';
import { convertToBoolean } from '@/utils/string';
import { isCaymanUser } from '@/utils/user';

import { ConfirmQuestion } from '../../components/ConfirmQuestion';
import { PreQuestions } from '../../components/PreQuestions';
import { UnemployedConfirmation } from '../../components/UnemployedConfirmation';
import { EmployerForm } from './EmployerForm';
import {
  buildRequestPayload,
  EmploymentInfoForm,
  getDefaultEmployerFormValues,
} from './helper';
import { CaymanSelfEmployedForm } from './SelfEmployedForm/CaymanSelfEmployedForm';
import { SelfEmployedForm } from './SelfEmployedForm/SelfEmployedForm';

import { DrawerContainer } from '../Drawers.styled';
import { StyledEmploymentInfoDrawer } from './EmploymentInfo.styled';

export function EmploymentInfoDrawer({ ...props }: DrawerProps) {
  const { t } = useTranslate();
  const user = useAppSelector(selectLoggedInUser);
  const {
    showRiskSectorFlagQuestion,
    showSelfEmployedStartupQuestion,
    showSelfEmployedWithRiskSectorFlagQuestion,
  } = useAppSelector(selectConfigsState);
  const formMethods = useForm<EmploymentInfoForm>({
    defaultValues: getDefaultEmployerFormValues(user),
    mode: 'onChange',
  });

  const { handleSubmit, setValue, watch, reset: resetForm } = formMethods;
  const employmentStatus = watch('employmentDetailed.employmentStatus');
  const watchedRiskSectorFlag = watch('riskSectorFlag');
  const isStartup = watch('isStartup');
  const employerCountry = watch('employmentDetailed.employerCountry');

  const [showUnemployedConfirmation, setShowUnemployedConfirmation] =
    useState(false);
  const [pendingEmploymentOption, setPendingEmploymentOption] =
    useState<Option | null>(null);

  const hasBusinessPurposeAccount = user?.accountTypes?.some(
    account => account.isBusinessPurpose === true,
  );

  const { mutate, isPending, error, reset } = useMutationWithUserData({
    mutationFn: updateEmploymentInfo,
  });

  useEffect(() => {
    if (props.open) {
      resetForm();
      reset();
      setShowUnemployedConfirmation(false);
      setPendingEmploymentOption(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  const onSubmit = handleSubmit(payload => {
    mutate(buildRequestPayload({ user, payload }));
  });

  const onEmployerFormClose = () => {
    setValue(
      'employmentDetailed',
      getDefaultEmployerFormValues(user).employmentDetailed,
    );
    setValue('employmentDetailed.employmentStatus', undefined);
  };

  const onListedCategoriesNo = () => {
    setValue('riskSectorFlag', false);
    onSubmit();
  };

  const onListedCategoriesYes = () => {
    setValue('riskSectorFlag', true);
    onSubmit();
  };

  const onSetEmploymentStatus = (option: Option) => {
    if (
      option.value === EmploymentStatus.Unemployed &&
      hasBusinessPurposeAccount &&
      !isCaymanUser(user)
    ) {
      setPendingEmploymentOption(option);
      setShowUnemployedConfirmation(true);
      return;
    }

    setValue(
      'employmentDetailed.employmentStatus',
      option.value as EmploymentStatus,
    );
  };

  const onConfirmUnemployedSelection = () => {
    if (pendingEmploymentOption) {
      setValue(
        'employmentDetailed.employmentStatus',
        pendingEmploymentOption.value as EmploymentStatus,
      );
    }
    setShowUnemployedConfirmation(false);
    setPendingEmploymentOption(null);
  };

  const onCancelUnemployedSelection = () => {
    setShowUnemployedConfirmation(false);
    setPendingEmploymentOption(null);
  };

  const onSetEmploymentDesc = (option: Option) => {
    setValue('employmentDetailed.employmentDesc', option.value as Profession);
    onSubmit();
  };

  const drawerBgColor = (theme: Theme) => {
    if (showUnemployedConfirmation) {
      return theme.palette.neutral[300];
    }

    return employmentStatus === EmploymentStatus.Employed
      ? theme.palette.background.paper
      : theme.palette.neutral[100];
  };

  const renderQuestions = () => {
    if (!user) return null;

    if (showUnemployedConfirmation) {
      return (
        <UnemployedConfirmation
          onConfirm={onConfirmUnemployedSelection}
          onCancel={onCancelUnemployedSelection}
        />
      );
    }

    if (!employmentStatus) {
      return (
        <PreQuestions
          question={t('moreInfo.employmentInfoDrawer.preQuestions.title')}
          options={getEmploymentStatusOptions()}
          onSelect={onSetEmploymentStatus}
          colorTheme="primary"
        />
      );
    }

    if (
      employmentStatus === EmploymentStatus.Employed &&
      !user.employmentDetailed?.employerCountry
    ) {
      return (
        <FormProvider {...formMethods}>
          <EmployerForm onClose={onEmployerFormClose} onSubmit={onSubmit} />
        </FormProvider>
      );
    }

    if (
      isCaymanUser(user) &&
      employmentStatus === EmploymentStatus.SelfEmployed
    ) {
      return (
        <FormProvider {...formMethods}>
          <CaymanSelfEmployedForm onSubmit={onSubmit} />
        </FormProvider>
      );
    }

    if (
      employmentStatus === EmploymentStatus.SelfEmployed &&
      isNil(watchedRiskSectorFlag) &&
      showRiskSectorFlagQuestion
    ) {
      return (
        <ConfirmQuestion
          question={t(LISTED_CATEGORIES_QUESTION.question)}
          description={t(LISTED_CATEGORIES_QUESTION.description)}
          onNo={onListedCategoriesNo}
          onYes={onListedCategoriesYes}
        />
      );
    }

    if (
      employmentStatus === EmploymentStatus.SelfEmployed &&
      ((convertToBoolean(watchedRiskSectorFlag) === false &&
        (isNil(isStartup) || employerCountry === '') &&
        showSelfEmployedStartupQuestion) ||
        (showSelfEmployedWithRiskSectorFlagQuestion &&
          isNil(user?.riskSectorFlag)))
    ) {
      return (
        <FormProvider {...formMethods}>
          <SelfEmployedForm onSubmit={onSubmit} />
        </FormProvider>
      );
    }

    let employmentQuestionConfig: ProfessionQuestionConfig | null = null;
    if (employmentStatus === EmploymentStatus.SelfEmployed) {
      if (!isCaymanUser(user)) {
        return null;
      }
      employmentQuestionConfig = getProfessionQuestionForSelfEmployed(
        user?.tenant,
      );
    } else if (employmentStatus === EmploymentStatus.Unemployed) {
      employmentQuestionConfig = getUnemployedProfessions();
    }

    return (
      <PreQuestions
        question={t(employmentQuestionConfig?.question || '')}
        options={employmentQuestionConfig?.options}
        onSelect={onSetEmploymentDesc}
        colorTheme="primary"
      />
    );
  };

  return (
    <StyledEmploymentInfoDrawer
      {...props}
      bgColor={drawerBgColor}
      headerBgColor={drawerBgColor}
    >
      <DrawerContainer>
        {renderQuestions()}
        {error && (
          <Alert icon={<Icon name="alert" />} color="error" variant="standard">
            {getAPIErrorMessage(error)}
          </Alert>
        )}
      </DrawerContainer>
      {isPending && <FullPageLoaderOverlay />}
    </StyledEmploymentInfoDrawer>
  );
}
